<template lang="pug">
form.screen-ordering-question.question.dont-unlock.text-lg.w-full.break-inside-avoid(
  :class="{ '-is-submitted': isSubmitted, '-all-correct': isSubmitted && allCorrect }"
  :data-completed="isSubmitted"
  :id="`q_${screen.id}`"
  :data-id="screen.id"
  ref="elRef"
)
  transition-expand
    .question__text.prose-stack.mb-8.fade-in(
      :class="[{ 'mx-auto': screen.center }, screen.styles?.question]"
      v-if="!isSubmitted || !overlay"
    )
      uc-editable.font-bold(
        :editPath="`${editPath}.question`"
        :k="`${screen.id}.question`"
        :text="screen.question"
      )
  .flex.flex-col.w-full.gap-16(
    :class="{ 'md_flex-row': !vertical && !overlay }"
    v-if="!audioDoc"
  )
    uc-preload-sfx(name="click1")
    uc-preload-sfx(name="select")
    transition-expand
      .flex-1.fade-in(v-if="!isSubmitted || !overlay")
        fieldset.flex-1(:disabled="isSubmitted")
          SlickList.slick-list.flex.flex-wrap.w-full.question__options(
            :appendTo="`#q_${screen.id}`"
            :class="isSubmitted ? null : dragging ? 'cursor-grabbing' : 'cursor-grab'"
            :use-drag-handle="true"
            axis="y"
            tag="ul"
            v-model:list="options"
          )
            //- padding instead of gap prevents jumping
            SlickItem.slick-item.w-full.relative.z-0.py-2(
              :disabled="isSubmitted"
              :index="i"
              :key="i"
              :style="{ order: order(option) }"
              style="list-style: none"
              tag="li"
              v-for="(option, i) in options"
            )
              .fill.z-10.m-auto.me-20(
                @mousedown="startDrag"
                v-handle="true"
                v-if="!isSubmitted"
              )
              .w-full.rounded.flex.shadow
                .question-option.w-full.flex.items-center.transition.bg-paper(
                  :class="[twMerge('ripple_bg-white/20 p-1', isSelected(i) && 'bg-primary-500', isSubmitted && isSelected(i) && 'saturate-[0.1]')]"
                )
                  .ripple__wave

                  .collapsed-items.mx-2.h-full(class="!w-8")
                    .collapsed-item.h-full.inline-flex.items-center.justify-center(
                      :class="isSubmitted ? 'auto-alpha-0' : 'auto-alpha-1'"
                    )
                      uc-icon.text-gray-800.dark_text-white.text-xl.scale-150.opacity-20(
                        icon="ic:baseline-drag-indicator"
                      )

                    .collapsed-item.h-full.inline-flex.items-center.justify-center(
                      :class="!isSubmitted ? 'auto-alpha-0' : isCorrect(option, i) ? 'auto-alpha-1' : 'auto-alpha-0'"
                    )
                      .inline-flex.items-center.justify-center.auto-alpha-1.rounded-full.aspect-square.w-8.text-primary-500.bg-primary-500
                        uc-text.sr-only(text="Correct selected" v-if="!(wordDoc || audioDoc)")
                        uc-icon.text-white(icon="theme:check")

                  .question-option__text.p-2(
                    :uc-word-bold="!wordDoc && isCorrect(option, i) ? true : null"
                  )
                    div(v-if="highlightAnswers")
                      uc-text(
                        uc-word-bold="true"
                        uc-word-only=""
                        :text="`[${correctPosition(option)}]`"
                      )

                    uc-text.opacity-80.select-none.prose-stack(
                      :class="{ 'opacity-100 pr-0 text-black dark_text-white': isSubmitted && isCorrect(option, i) }"
                      :k="`${option.id}.${getOptionKey(option)}`"
                      :text="getContent(option)"
                      class="pr-[1ch]"
                    )
                  //- expose these strings for the translate script
                  //- text="Move item up one"
                  //- text="Move item down one"
                  .flex.z-20.justify-end.leading-none.text-xs.text-primary-600.me-2(
                    :class="isSubmitted ? 'auto-alpha-0' : 'auto-alpha-1'"
                    uc-word-skip=""
                  )
                    uc-text.sr-only(
                      :text="`Position ${i + 1} of ${screen.options.length}`"
                    )
                    button.inline-flex.items-center.justify-center.transition.ripple.ripple--centered.px-2.h-12.text-xl.duration-1000.focus-visible_ring(
                      :class="{ 'saturate-0 opacity-25': atTheStart(i) }"
                      :data-order="i"
                      :disabled="atTheStart(i)"
                      @click="up"
                      :aria-label="hasTranslation('Move item up one')"
                      class="btn-hover_bg-primary-500/20"
                      type="button"
                      data-sfx="click1"
                    ) 
                      uc-icon.z-0.dark_text-white.rtl_-scale-x-100(
                        icon="theme:up"
                      )

                    button.z-20.inline-flex.items-center.justify-center.transition.ripple.ripple--centered.px-2.h-12.text-lg.duration-1000.focus-visible_ring(
                      :class="{ 'saturate-0 opacity-25': atTheEnd(i) }"
                      :data-order="i"
                      :disabled="atTheEnd(i)"
                      @click="down"
                      :aria-label="hasTranslation('Move item down one')"
                      class="btn-hover_bg-primary-500/20"
                      type="button"
                      data-sfx="click1"
                    ) 
                      uc-icon.dark_text-white.rtl_-scale-x-100(
                        icon="theme:down"
                      )

        .question__submit-wrapper(
          :class="[{ 'hidden': isSubmitted }]"
        )
          .group.mt-8.question__submit-btn.flex.items-center.gap-6(
            :class="smallDevice && 'flex-wrap gap-2'"
          )
            uc-preload-sfx(name="yes")
            uc-preload-sfx(name="wrong")
            uc-button.btn.btn-primary.question__submit(
              :class="smallDevice && 'w-full'"
              :disabled="disableSubmit"
              ref="submit"
              @click="submitHandler"
              :data-sfx="allCorrect ? 'yes' : 'wrong'"
              text="Submit"
            )

    .question__feedback.flex-1.fade-in(
      ref="feedbackRef" 
      v-if="feedback && !(wordDoc || audioDoc)"
      aria-live="polite"
      aria-atomic="true"
      role="status"
      )
      transition-expand(
        :delay="overlay ? 0 : 0.5" 
        :duration="1" 
        scroll=""
        v-if="!lightbox"
        )
        .question__feedback-content.outline-2.block(
          v-if="isSubmitted"
        )
          //- expose these strings for the translate script
          //- text="Feedback"
          //- text="That’s right!"
          //- text="Not quite."
          uc-text.prose.md_prose-lg.text-primary-700.dark_text-primary-300.font-bold.italic.feedback-heading.mb-4(
            :class="[{ 'mx-auto': screen.center }, screen.styles?.question]"
            :k="isSurvey ? 'Feedback' : allCorrect ? 'That’s right!' : 'Not quite.'"
            :text="isSurvey ? 'Feedback' : allCorrect ? 'That’s right!' : 'Not quite.'"
            type="p"
            uc-dont-read=""
            v-if="!hideFeedbackTitle"
          )
          uc-editable.prose.dark_prose-invert(
            :class="[{ 'mx-auto': screen.center }, screen.styles?.text]"
            :editPath="`${editPath}.${feedback.path}`"
            :k="`${screen.id}.${feedback.path}`"
            :text="feedback.text"
            type="p"
          )
          slot

      //- show feedback button (for lightbox feedback)
      .group.question__retry-btn.flex.items-center.gap-6.pt-2(
        :class="smallDevice && 'flex-wrap gap-2'"
        v-if="lightbox && isSubmitted"
      )
        uc-button.btn.btn-primary.primary-green.question__retry(
          :class="smallDevice && 'w-full'"
          @click="showFeedback"
          text="Show feedback"
          data-sfx="continue"
        )

  //- render all conditional feedback (if any) for word docs and audio docs
  div(v-for="[k, v] in allConditionalFeedback" v-if="!lightbox && (wordDoc || audioDoc)")
    template(v-if="v.trim()")
      audio-marker(:name="`${screen.id}-${k}`" v-if="k === 'feedback'")
      audio-marker(:name="`${screen.id}-feedback-${k}`" v-else="")
      p(uc-dont-read="" v-if="!audioDoc")
        uc-text.font-bold(text="Feedback")

        span &nbsp;

        //- expose these strings for the translate script
        //- text="(correct)"
        //- text="(incorrect)"
        uc-text.font-bold(:text="`(${k})`" type="span" v-if="k !== 'feedback'") 

      uc-text(:k="`${screen.id}.feedback`" :text="v" type="p" v-if="k === 'feedback'")
      uc-text(:k="`${screen.id}.feedback.${k}`" :text="v" type="p" v-else="")
</template>

<script setup lang="ts">
import { nextTick, ref, computed, inject, watch, toRef } from 'vue';
import { set, setCompleted } from '../lib/SCORM';
import gsap from 'gsap';
import useSuspend from '../lib/useSuspend';
import useTranslations from '../stores/useTranslations';
import { play } from '../lib/audio';
import twMerge from '../lib/mergeStyles';
import useQuizzes from '../stores/useQuizzes';
import {
  SlickList,
  SlickItem,
  HandleDirective as vHandle
} from 'vue-slicksort';
import shuffle from '../lib/shuffle';
import {
  breakpointsTailwind,
  useBreakpoints,
  useEventListener
} from '@vueuse/core';
import openLightbox from '../lib/openLightbox';
import { saveInsightsData } from '../lib/insightsData';
import useScreenAudio from '../lib/useScreenAudio';
import orderingQuestionSchema from '../lib/screen-schemas/screen-ordering-question.schema';

const wordDoc = inject('wordDoc') as boolean;
const audioDoc = inject('audioDoc') as boolean;
const highlightAnswers = inject('highlightAnswers') as boolean;

type Option = ScreenQuestion['options'][0];

const props = defineProps<{ editPath: string; screen: ScreenQuestion }>();
const submit = ref(null);
const overlay = computed(() => props.screen['feedback-layout'] === 'replace');
const vertical = computed(() => props.screen['feedback-layout'] === 'bottom');
const isSurvey = computed(() => props.screen?.survey === true);
const lightbox = computed(() => props.screen['feedback-layout'] === 'lightbox');
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');

const suspend = useSuspend();
const { translations } = useTranslations();
const sectionId = inject('sectionId') as string;
const elRef = ref<HTMLElement>();

const options = ref(shuffle(props.screen.options));

const pageId = inject('pageId') as object;

watch(
  () => props.screen.options,
  () => {
    options.value = shuffle(props.screen.options);
  }
);

const dragging = ref(false);
const startDrag = (e: MouseEvent) => {
  const el = e.currentTarget as HTMLElement;
  const item = el.closest('.slick-item');

  if (item) {
    item.classList.add('slick-item-dragging');
  }
  return (dragging.value = true);
};

const endDrag = (e: MouseEvent) => {
  const items = elRef.value.querySelectorAll('.slick-item');

  items.forEach(item => {
    item.classList.remove('slick-item-dragging');
  });

  return (dragging.value = false);
};

useEventListener(window, 'mouseup', endDrag);

// const pressed = useMousePressed({ target: elRef });

const { register } = useQuizzes();

register(props.screen, pageId);

const selected = computed(() => {
  return suspend.questionOptions[props.screen.id] || [];
});

const isSubmitted = computed(() => {
  return Boolean(suspend.completed[props.screen.id]);
});

const disableSubmit = computed(() => (isSubmitted.value ? true : false));

const hideFeedbackTitle = computed(() => {
  return props.screen['feedback-no-title'] ?? false;
});

const allCorrect = computed(() => {
  let result = true;
  options.value.forEach((o, i) => {
    const checked = isCorrect(o, i);
    if (!checked) {
      result = false;
    }
  });
  return result;
});

const allConditionalFeedback = computed<[string, string][]>(() => {
  if (typeof props.screen.feedback === 'string')
    return [['feedback', props.screen.feedback]];

  return Object.entries(props.screen.feedback || {});
});

async function submitHandler(e: Event) {
  const { id } = props.screen;

  // we need to apply the delay before completion as it relies on a transtion-delay for the auto-alpha-1 CSS class
  delayNextButton(feedback.value ? 1 : 0.5);

  setCompleted(id);

  await nextTick();

  set(`questionOptions.${id}`, selected.value);
  set(`correctQuestions.${id}`, allCorrect.value);

  await new Promise(r => gsap.delayedCall(props.screen.feedback ? 0.5 : 0, r));

  setCompleted(sectionId);

  saveInsightsData(props.screen, {
    correct: allCorrect.value
  });

  if (lightbox.value) {
    openLightbox(feedback.value.text);
  } else {
    playFeedbackAudio();
  }
}

function delayNextButton(delay) {
  const section = elRef.value?.closest('.section') as HTMLElement;
  const nextButton = section?.querySelector(
    '.screen-next-button'
  ) as HTMLElement;

  if (nextButton)
    nextButton.style.transitionDelay = suspend.reducedMotion
      ? '0s'
      : `${delay}s`;
}

// use flex order to shuffle options or place in correct order when submited (unless Q type is a survey)
function order(option: Option) {
  return isSubmitted.value && !isSurvey.value ? correctPosition(option) : 0;
}

function correctPosition(option: Option) {
  return Object.keys(option)[0];
}

/**
 * Returns an object `{ text, path }`, computed from the given feedback prop and the status of the user's selection.
 *
 * The `text` is the relevant feedback string to display. The `path` is where in the YAML the string was found, for the purpose of finding its translation key, e.g., `feeback` or `feedback.correct`.
 *
 * @param f The feedback string, or object when conditional
 */
function getFeedbackString(f: ScreenQuestion['feedback']): {
  text: string;
  path: string;
  audio: string;
} | null {
  // no feedback
  if (!f) return null;

  // single feedback text
  if (typeof f === 'string')
    return { text: f, path: 'feedback', audio: 'feedback' };

  // correct
  if (f.correct && allCorrect.value)
    return {
      text: f.correct,
      path: 'feedback.correct',
      audio: 'feedback-correct'
    };

  // option-specific
  let i = props.screen.options.length;

  while (i--) {
    if (f[i + 1] && isSelected(i)) {
      return {
        text: f[i + 1],
        path: 'feedback.' + (i + 1),
        audio: 'feedback-' + (i + 1)
      };
    }
  }

  // incorrect
  if (f.incorrect && !allCorrect.value)
    return {
      text: f.incorrect,
      path: 'feedback.incorrect',
      audio: 'feedback-incorrect'
    };

  return null;
}

const feedback = computed(() => getFeedbackString(props.screen.feedback));

function showFeedback() {
  if (lightbox.value) {
    openLightbox(feedback.value.text);
  }
}

const { playSlot } = useScreenAudio(
  toRef(props, 'screen'),
  orderingQuestionSchema
);

function playFeedbackAudio() {
  // Tier 1: explicit prop-level feedback-audio / feedback-speech
  const f = props.screen['feedback-audio'] || props.screen['feedback-speech'];
  const fs = getFeedbackString(f);

  if (fs?.text) {
    play(fs.text as string);
    return;
  }

  // Tier 2/3: schema-based — resolveSlot handles S3 (getAudioURL) or JiT
  const slotName =
    getFeedbackString(props.screen.feedback)?.audio ?? 'feedback';
  playSlot(slotName);
}

function getOptionId(i: number) {
  return `${props.screen.id}_o${i}`;
}

function getContent(option: Option) {
  return option[Object.keys(option)[0]];
}

function isCorrect(option: Option, i: number) {
  const optionOrder = i + 1;
  return Object.keys(option)[0] === optionOrder.toString();
}

function isSelected(i: number) {
  return selected.value.includes(getOptionId(i));
}

function getOptionKey(option: Option) {
  return Object.keys(option)[0];
}

// for disabling prev at the start
function atTheStart(i) {
  return i === 0;
}

// for disabling next at the end
function atTheEnd(i) {
  return props.screen.options.length === i + 1;
}

function reorderItem(arr: any[], fromIndex: number, toIndex: number) {
  var item = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, item);

  return [...arr];
}

function up(e: MouseEvent) {
  // if (e) e.preventDefault();
  const el = e.currentTarget as HTMLElement;
  const currentOrder = Number(el.dataset.order);
  if (currentOrder === 0) return;

  options.value = reorderItem(options.value, currentOrder, currentOrder - 1);
}

function down(e: MouseEvent) {
  // if (e) e.preventDefault();
  const el = e.currentTarget as HTMLElement;
  const currentOrder = Number(el.dataset.order);
  if (currentOrder === props.screen.options.length - 1) return;

  options.value = reorderItem(options.value, currentOrder, currentOrder + 1);
}

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || key;
}
</script>

<style scoped>
.question-option {
  min-width: 20ch;
  transition: 0.2s;
}

.question-option__text {
  @apply w-full pr-1;
}

fieldset:not(:disabled) .question-option__background.fill:hover {
  @apply bg-primary-500/10;
}

.question-option .ripple__wave {
  @apply bg-primary-500/10;
}

li.slick-item.slick-item-dragging .shadow {
  @apply shadow-lg;
}
</style>
